import { Component, prop } from "@kluntje/core";
import { render } from "lit-html";
import { I18nService } from "Services/I18nService";
import { fetchJSON } from "Helper/apiHelper";
import { autobind } from "Helper/functionHelper/decorators";
import { addClass, removeClass, waitForTransitionEnd } from "@kluntje/js-utils/lib/dom-helpers";
import { HIDDEN } from "Helper/cssClasses";

import {
  loadMoreButton,
  noOrdersAvailable,
  orderDetailsTemplate,
  ordersListTemplate,
} from "./fra-m-orders__list.template";// eslint-disable-line

const SLIDE_OUT = "fra-m-orders__details-wrapper--slide-out";

const ORDER_HASH_PREFIX = "#order-";
export function createHashFromOrderCode(orderCode: string) {
  return ORDER_HASH_PREFIX + orderCode;
}

export function getOrderCodeFromHash(hash: string) {
  return hash.replace(ORDER_HASH_PREFIX, "");
}

class FraMOrders extends Component {
  private ordersListHasRendered = false;

  @prop({ required: true })
  dataApiOverviewUrl = "";

  @prop({ required: true })
  dataApiDetailsUrl = "";

  constructor() {
    super({
      ui: {
        ordersList: ".fra-m-orders__list:-one",
        orderDetails: ".fra-m-orders__details:-one",
        orderOverviewWrapper: ".fra-m-orders__overview-wrapper:-one",
        orderDetailsWrapper: ".fra-m-orders__details-wrapper:-one",
        orderBackButton: ".fra-js-orders__back:-one",
        loadMoreOrdersButton: ".fra-m-orders__load-more-button:-one",
      },
      events: [
        {
          event: "click",
          target: "orderBackButton",
          handler: "handleBackButton",
        },
        {
          event: "hashchange",
          target: "window",
          handler: "onHashChange",
        },
        {
          event: "popstate",
          target: "window",
          handler: "switchToOrderOverviewData",
        },
      ],
      initialStates: {
        // "not fetched" as initial value to detect empty order Array as change
        orders: ["not fetched"],
        showOrdersUpTo: 8,
      },
      reactions: {
        orders: ["handleDataUpdate"],
        showOrdersUpTo: ["handleDataUpdate"],
      },
    });
  }

  /**
   * @override
   */
  afterComponentRender(): void {
    this.fetchOrders();
  }

  /**
   * fetch json
   * @returns {Promise<void>}
   */
  async fetchOrders(): Promise<void> {
    const apiResponse = await fetchJSON(this.dataApiOverviewUrl);
    this.setState({ orders: apiResponse.records });
  }

  /**
   * Reaction on state change (e.g. json fetched)
   */
  handleDataUpdate(): void {
    // is a deeplink to detailed view of an order
    if (location.hash.startsWith(ORDER_HASH_PREFIX)) {
      this.renderOrderDetails(getOrderCodeFromHash(location.hash));
    } else {
      this.renderOrdersList();
    }
  }

  /**
   * Switches back to order overview
   * @param {UIEvent} _e
   */
  handleBackButton(_e: UIEvent | PopStateEvent): void {
    // i.e. not started with detail view opened on page load via a deeplink
    history.replaceState(null, "", location.href.replace(location.hash, ""));
    if (!this.ordersListHasRendered) {
      this.renderOrdersList();
    }
    this.switchToOrderOverviewData();
  }

  /**
   * Reaction to page change e.g. page forward
   * @param {HashChangeEvent} _e
   */
  onHashChange(_e: HashChangeEvent) {
    if (location.hash.startsWith(ORDER_HASH_PREFIX)) {
      const orderCode = getOrderCodeFromHash(location.hash);
      this.renderOrderDetails(orderCode);
    }
  }

  /**
   * calls template to render orders list
   * @returns {Promise<void>}
   */
  async renderOrdersList(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    if (this.state.orders.length === 0) {
      render(noOrdersAvailable(), this.ui.ordersList);
    } else {
      const ordersList = ordersListTemplate(
        this.state.orders,
        this.toggleLoadMoreButton,
        this.state.showOrdersUpTo,
        this.renderOrderDetails,
      );
      render(ordersList, this.ui.ordersList);
      this.ordersListHasRendered = true;
    }
  }

  /**
   * calls template to render order detail list
   * @param {string} orderNumber
   * @returns {Promise<void>}
   */
  @autobind
  async renderOrderDetails(orderNumber: string): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const orderDetailsUrl = this.dataApiDetailsUrl.replace("ORDER_NUMBER", orderNumber);
    const orderData = await fetchJSON(orderDetailsUrl);
    history.pushState(null, "", createHashFromOrderCode(orderNumber));
    this.switchToOrderDetailsData();
    render(orderDetailsTemplate(orderData.records[0]), this.ui.orderDetails);
  }

  /**
   * shows load more orders button
   * @param {number} showOrdersUpTo
   */
  @autobind
  toggleLoadMoreButton(showOrdersUpTo: number): void {
    if (this.state.orders.length <= showOrdersUpTo) {
      addClass(this.ui.loadMoreOrdersButton, HIDDEN);
    } else {
      render(loadMoreButton(this.setState.bind(this), showOrdersUpTo), this.ui.loadMoreOrdersButton);
    }
  }

  /**
   * animates switch to details view
   */
  switchToOrderDetailsData(): void {
    addClass(this.ui.orderOverviewWrapper, HIDDEN);
    addClass(this.ui.orderDetailsWrapper, SLIDE_OUT);
    removeClass(this.ui.orderDetailsWrapper, HIDDEN);
    // double raf to change after the current styling is applied
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        removeClass(this.ui.orderDetailsWrapper, SLIDE_OUT);
        window.scrollTo(0, 0);
      }),
    );
  }

  /**
   * animates switch to overview view
   */
  async switchToOrderOverviewData(): Promise<void> {
    addClass(this.ui.orderDetailsWrapper, SLIDE_OUT);
    await waitForTransitionEnd(this.ui.orderDetailsWrapper);
    removeClass(this.ui.orderOverviewWrapper, HIDDEN);
    removeClass(this.ui.orderDetailsWrapper, SLIDE_OUT);
    addClass(this.ui.orderDetailsWrapper, HIDDEN);
  }
}

customElements.define("fra-m-orders", FraMOrders);
export default FraMOrders;
